import React, { useEffect, useState, useRef } from "react";
import { UnlockVaultPanel } from "./unlock-vault-panel";
import { useVaultStore } from "@/stores/vault-store";
import { Button } from "@secure-vault/ui";
import { Input } from "@secure-vault/ui";
import { ConfirmDialog } from "@secure-vault/ui";
import {
  initiateVaultFile,
  uploadVaultFile,
  downloadVaultFile,
  listVaultFiles,
  deleteVaultFile,
} from "@secure-vault/api";
import { FileListItem } from "@secure-vault/api/types";
import {
  encryptFilePayload,
  decryptFilePayload,
  generateRandomBytes,
  encryptWithKey,
  decryptWithKey,
} from "@/lib/crypto/masterKey";
import {
  FileText,
  Download,
  Trash2,
  Upload,
  Search,
  Eye,
  Loader2,
  X,
  FileCheck,
  AlertTriangle,
  FolderOpen,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDateFormat } from "@/hooks/use-date-format";

export function VaultFilesContainer() {
  const { formatDateTime } = useDateFormat();
  const { isUnlocked, masterKey } = useVaultStore();

  const [files, setFiles] = useState<FileListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadProgress, setUploadProgress] = useState<{
    name: string;
    progress: number;
  } | null>(null);

  // Preview State
  const [previewFile, setPreviewFile] = useState<FileListItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!isUnlocked) return;
    loadFiles();
  }, [isUnlocked]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const fileList = await listVaultFiles();
      setFiles(fileList);
    } catch (err) {
      console.error("Failed to load files:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Upload Logic
  const handleUpload = async (file: File) => {
    if (!masterKey) return;
    setUploadProgress({ name: file.name, progress: 10 });

    try {
      // 1. Generate local AES-GCM file key (32 bytes)
      const fileKey = generateRandomBytes(32);

      // 2. Encrypt the file key with the vault master key
      setUploadProgress({ name: file.name, progress: 30 });
      const encryptedFileKey = await encryptWithKey(fileKey, masterKey);

      // 3. Encrypt the file data locally using the file key
      setUploadProgress({ name: file.name, progress: 50 });
      const encryptedBlob = await encryptFilePayload(file, fileKey);

      // 4. Initiate upload on the backend API
      setUploadProgress({ name: file.name, progress: 70 });
      const initiateResponse = await initiateVaultFile({
        original_filename: file.name,
        mime_type: file.type || "application/octet-stream",
        encrypted_file_key: encryptedFileKey,
      });

      // 5. Upload the local encrypted payload blob to GCS proxy
      setUploadProgress({ name: file.name, progress: 90 });
      await uploadVaultFile(initiateResponse.file_id, encryptedBlob);

      setUploadProgress(null);
      loadFiles();
    } catch (err) {
      console.error("Encryption or upload failed:", err);
      setAlertConfig({
        title: "We couldn't add this document",
        message: "Please try again. If the problem continues, contact support.",
      });
      setUploadProgress(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  // Download & Decrypt Logic
  const handleDownload = async (file: FileListItem) => {
    if (!masterKey) return;
    try {
      // 1. Decrypt the file key using our master key
      const fileKey = await decryptWithKey(file.encrypted_file_key, masterKey);

      // 2. Download the encrypted file blob
      const encryptedBlob = await downloadVaultFile(file.file_id);

      // 3. Decrypt the file blob using the file key
      const decryptedBlob = await decryptFilePayload(
        encryptedBlob,
        fileKey,
        file.mime_type || undefined
      );

      // 4. Trigger browser download
      const url = URL.createObjectURL(decryptedBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename || "downloaded-file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Decryption or download failed:", err);
      setAlertConfig({
        title: "We couldn't download this document",
        message: "Please try again. If the problem continues, contact support.",
      });
    }
  };

  // Preview Logic
  const handlePreview = async (file: FileListItem) => {
    if (!masterKey) return;
    setPreviewFile(file);
    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      const fileKey = await decryptWithKey(file.encrypted_file_key, masterKey);
      const encryptedBlob = await downloadVaultFile(file.file_id);
      const decryptedBlob = await decryptFilePayload(
        encryptedBlob,
        fileKey,
        file.mime_type || undefined
      );

      const url = URL.createObjectURL(decryptedBlob);
      setPreviewUrl(url);
    } catch (err) {
      console.error("Preview decryption failed:", err);
      setAlertConfig({
        title: "We couldn't open this document",
        message: "Try downloading it instead, or try again in a moment.",
      });
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  // Delete Logic
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async () => {
    if (!fileToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteVaultFile(fileToDelete);
      setFileToDelete(null);
      loadFiles();
    } catch (err) {
      console.error("Failed to delete file:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredFiles = files.filter((f) => {
    const filename = f.original_filename || "";
    return filename.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (!isUnlocked) {
    return <UnlockVaultPanel />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className="space-y-0.5">
          <h1 className="text-2xl font-bold tracking-tight">Your documents</h1>
          <p className="text-muted-foreground text-sm">
            Add and view the important documents kept in your vault.
          </p>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Drag & Drop Upload Dropzone */}
      <div
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative cursor-pointer overflow-hidden rounded-xl border-2 border-dashed p-8 text-center transition-all duration-300 ${
          dragActive
            ? "border-primary bg-accent"
            : "border-muted bg-card hover:border-muted-foreground/50"
        }`}
      >
        <div className="flex flex-col items-center justify-center space-y-3">
          <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
            <Upload className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold">
              Drag a document here, or click to choose one
            </p>
            <p className="text-muted-foreground text-xs">
              Your document will be protected before it is stored.
            </p>
          </div>
        </div>
      </div>

      {/* Uploading progress bar */}
      <AnimatePresence>
        {uploadProgress && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border-primary/20 bg-primary/5 rounded-xl border p-4"
          >
            <div className="space-y-2">
              <div className="text-primary flex justify-between text-xs font-semibold">
                <span className="max-w-[80%] truncate">
                  Adding {uploadProgress.name}
                </span>
                <span>{uploadProgress.progress}%</span>
              </div>
              <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
                <div
                  className="bg-primary h-full transition-all duration-300"
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search & Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className="pl-10"
          />
        </div>
      </div>

      {/* File List / Grid */}
      <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center space-y-4 py-24">
            <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
            <p className="text-muted-foreground text-sm">
              Loading your documents...
            </p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center space-y-3 py-24 text-center">
            <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
              <FolderOpen className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold">No documents found</p>
              <p className="text-muted-foreground text-xs">
                Add an important document to get started.
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y">
            {filteredFiles.map((file) => (
              <div
                key={file.file_id}
                className="hover:bg-muted/50 flex items-center justify-between p-4 transition-colors"
              >
                <div className="flex min-w-0 items-center gap-3.5">
                  <div className="bg-muted text-muted-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <p className="max-w-md truncate text-sm font-medium">
                      {file.original_filename}
                    </p>
                    <div className="text-muted-foreground flex items-center gap-2.5 text-xs">
                      <span>
                        {file.file_size != null
                          ? formatBytes(file.file_size)
                          : "Pending"}
                      </span>
                      <span>•</span>
                      <span>{formatDateTime(file.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => handlePreview(file)}
                    title="Open document"
                  >
                    <Eye className="h-4.5 w-4.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => handleDownload(file)}
                    title="Download document"
                  >
                    <Download className="h-4.5 w-4.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setFileToDelete(file.file_id)}
                    title="Delete document"
                  >
                    <Trash2 className="h-4.5 w-4.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal Overlay */}
      <AnimatePresence>
        {previewFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              className="bg-card text-card-foreground flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border shadow-lg"
            >
              {/* Header */}
              <div className="bg-muted/30 flex items-center justify-between border-b p-4">
                <div className="flex items-center gap-2">
                  <FileCheck className="text-primary h-5 w-5" />
                  <span className="max-w-lg truncate font-semibold">
                    {previewFile.original_filename}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={handleClosePreview}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Viewport */}
              <div className="bg-muted/10 flex flex-1 items-center justify-center overflow-auto">
                {previewLoading ? (
                  <div className="flex flex-col items-center space-y-3">
                    <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
                    <p className="text-muted-foreground text-sm">
                      Opening your document...
                    </p>
                  </div>
                ) : previewUrl ? (
                  /* Decrypted previews */
                  previewFile.mime_type?.startsWith("image/") ? (
                    <img
                      src={previewUrl}
                      alt={previewFile.original_filename || undefined}
                      className="max-h-full max-w-full object-contain p-4"
                    />
                  ) : previewFile.mime_type === "application/pdf" ? (
                    <iframe
                      src={previewUrl}
                      title={previewFile.original_filename || undefined}
                      className="h-full w-full border-0"
                    />
                  ) : (
                    /* Fallback: cannot render, show download button */
                    <div className="max-w-md space-y-4 p-8 text-center">
                      <div className="bg-muted text-muted-foreground mx-auto flex h-14 w-14 items-center justify-center rounded-full border">
                        <AlertTriangle className="h-6 w-6" />
                      </div>
                      <div className="space-y-1.5">
                        <h3 className="text-base font-bold">
                          Preview not available
                        </h3>
                        <p className="text-muted-foreground text-xs">
                          This type of document cannot be shown here. You can
                          download it to open it instead.
                        </p>
                      </div>
                      <Button onClick={() => handleDownload(previewFile)}>
                        <Download className="mr-2 h-4 w-4" />
                        Download document
                      </Button>
                    </div>
                  )
                ) : (
                  <p className="text-muted-foreground text-sm">
                    We couldn't show this document. Please try downloading it.
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={fileToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
        title="Delete this document?"
        description="This will permanently remove the document from your vault. This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        loading={deleteLoading}
        disabled={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setFileToDelete(null)}
      />

      <ConfirmDialog
        open={alertConfig !== null}
        onOpenChange={(open) => {
          if (!open) setAlertConfig(null);
        }}
        title={alertConfig?.title || "Notification"}
        description={alertConfig?.message || ""}
        confirmLabel="OK"
        hideCancel
        onConfirm={() => setAlertConfig(null)}
      />
    </div>
  );
}
